import React from 'react';
import { TouchableOpacity } from 'react-native';
import { translate } from '../../services/translate';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useStyles } from 'react-native-unistyles';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import DataTableContainer from '../../components/DataTableContainer';
import MyDataTable from '../../components/MyDataTable';
import { Pagination } from '../../components/Pagination';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { DocumentsParamList } from '../../typings/Params';
import { documentService } from '../../services/central-api/document';
import { useQuery } from '@tanstack/react-query';
import { openExternalLinkInNewTab } from '../../services/navigation-assistant';

export default function Documents() {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<DocumentsParamList, 'Documents'>>();
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();

    const [filters, setFilters] = React.useState({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const isFocused = useIsFocused();
    const { data, isLoading } = useQuery({
        queryKey: ['getDocuments', filters],
        queryFn: () => documentService.getDocuments(filters),
        enabled: isFocused,
    });

    return <Container>
        <Header title={translate('documents')} />
        <Card>
            <DataTableHeader
                title={translate('registeredDocuments')}
                zIndex={2}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('title'),
                            selector: row => row.title,
                            wrap: true,
                        },
                        {
                            name: translate('user'),
                            selector: row => row.user.warName || row.user.name,
                            wrap: true,
                        },
                        {
                            name: translate('version'),
                            selector: row => row.version,
                        },
                        {
                            name: translate('category'),
                            selector: row => translate(row.category),
                            wrap: true,
                        },
                        {
                            name: translate('createdAt'),
                            selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                            wrap: true,
                        },
                        {
                            name: translate('action'),
                            button: true,
                            cell: row =>
                                <TouchableOpacity onPress={() => openExternalLinkInNewTab(row.path)}>
                                    <FontAwesomeIcon fontSize={16} color={theme.colors.iconColor} icon={faDownload} />
                                </TouchableOpacity>
                        },
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
    </Container>;
}
